<template>
  <PDialog
    v-model:visible="displayModal"
    :closable="true"
    :dismissable-mask="true"
    :draggable="false"
    :modal="true"
    :position="dialogPosition"
    :show-header="false"
    class="notifi-dialog-container"
    content-class="notifi-dialog-content"
  >
    <NotConnectedCard v-if="connected === false" />
    <NoAptosCard v-else-if="showNoAptosBanner" @on-close="onClose" />
    <NotifiSubscriptionCard
      v-else-if="connected"
      :connected="connected"
      :public-key="publicKey"
      :sign-message-params="signMessageParams"
      :wallet-address="walletAddress"
      card-id="72fba24b63ad4d39afc33d1fcb42fbb3"
      environment="Production"
      tenant-id="pontem.apt"
      @on-close="onClose"
    />
  </PDialog>
</template>
<script lang="ts" setup>
import { NotifiFrontendClient } from '@notifi-network/notifi-frontend-client';
import {
  NoAptosCard,
  NotConnectedCard,
  NotifiSubscriptionCard,
} from '@/notifi-vue-package/';

import { useWalletPlugin } from '@/composables/useWalletPlugin';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useCurrentAccountBalance } from '@/composables/useAccountBalance';
import { APTOS } from '@/constants';
import { isMobile as checkIfMobile } from '@/utils';

type IProps = {
  visible: boolean;
};
const props = defineProps<IProps>();

const { account, connected, signMessage } = useWalletPlugin();
const mainBalance = useCurrentAccountBalance(APTOS);
const showNoAptosBanner = computed(() => mainBalance.balance.value === 0);

const walletAddress = computed(() => {
  let rawAddress = account.value.address;
  if (typeof rawAddress !== 'string') {
    rawAddress = rawAddress.hex();
  }

  return rawAddress;
});

const publicKey = computed(() => {
  let rawPublicKey = account.value.publicKey;
  if (Array.isArray(rawPublicKey)) {
    rawPublicKey = rawPublicKey[0];
  }

  if (typeof rawPublicKey !== 'string') {
    rawPublicKey = rawPublicKey.hex();
  }

  return rawPublicKey;
});

const signMessageParams = computed(() => {
  const params: Parameters<NotifiFrontendClient['logIn']>[0] = {
    walletBlockchain: 'APTOS',
    signMessage: async (message, nonce) => {
      const result = await signMessage({
        message,
        nonce: `${nonce}`,
        address: true,
      });

      if (typeof result === 'string') {
        return result;
      }

      return result.signature;
    },
  };
  return params;
});

const emits = defineEmits(['update:visible']);
const displayModal = computed({
  get: () => !!props.visible,
  set: (v) => emits('update:visible', v),
});

function onClose() {
  displayModal.value = false;
}

watch(displayModal, async (visible) => {
  if (!visible) {
    return;
  }
});

const isMobile = ref(checkIfMobile());
const onWindowResize = () => {
  isMobile.value = checkIfMobile();
};
onMounted(() => {
  window.addEventListener('resize', onWindowResize);
});
onUnmounted(() => {
  window.removeEventListener('resize', onWindowResize);
});

const dialogPosition = computed(() => (isMobile.value ? 'top' : 'topright'));
</script>
